import { useMemo } from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { SPOTLIGHT_SURFACE_ATTRIBUTE } from "./useDomPresence";

/**
 * Onde o cartão de novidade fica na tela, nas duas formas que ele tem:
 *
 * - `AnchoredSurface`: à direita do item de menu, com uma seta apontando para ele, preso ao
 *   elemento (acompanha se ele se mover ou mudar de tamanho).
 * - `DockedSurface`: rodapé de largura total, para o celular, onde o menu lateral some.
 *
 * Nenhuma das duas bloqueia nada: sem sobreposição, sem prisão de foco, sem roubar o foco ao
 * aparecer, e Esc ou clique fora não fazem nada. É daqui que o host, e o "Testar na minha
 * conta" do admin, tiram a mesma moldura: o que o admin testa é o que a pessoa recebe.
 */

/**
 * @param {{ anchorEl: Element|null, titleId?: string, children: import("react").ReactNode }} props
 */
export function AnchoredSurface({ anchorEl, titleId, children }) {
  const anchorRef = useMemo(() => ({ current: anchorEl }), [anchorEl]);

  return (
    <Popover open>
      <PopoverAnchor virtualRef={anchorRef} />
      <PopoverContent
        side="right"
        align="start"
        sideOffset={14}
        aria-labelledby={titleId}
        data-variant="anchored"
        {...{ [SPOTLIGHT_SURFACE_ATTRIBUTE]: "" }}
        className="w-80 max-w-[calc(100vw-2rem)] border-border-strong p-0 shadow-lg"
        // Cartão sem modal: nada de levar o foco para dentro, devolvê-lo, ou fechar por Esc/clique fora.
        onOpenAutoFocus={(event) => event.preventDefault()}
        onCloseAutoFocus={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
      >
        {/* O Popover do Radix prende o Tab dentro dele (o `loop` do FocusScope é fixo), o que aqui
            seria uma armadilha de teclado (SPOT-06). O Tab é contido neste invólucro, antes de
            chegar nesse laço; a ação padrão do navegador (mover o foco) segue normalmente. */}
        <div onKeyDown={letTabLeave}>{children}</div>
        <PopoverPrimitive.Arrow data-spotlight-arrow width={18} height={9} className="fill-popover stroke-border-strong" />
      </PopoverContent>
    </Popover>
  );
}

/**
 * @param {{ titleId?: string, children: import("react").ReactNode }} props
 */
export function DockedSurface({ titleId, children }) {
  return (
    <div
      role="dialog"
      aria-labelledby={titleId}
      data-variant="dock"
      {...{ [SPOTLIGHT_SURFACE_ATTRIBUTE]: "" }}
      className="fixed inset-x-4 bottom-[max(1rem,env(safe-area-inset-bottom))] z-50 rounded-lg border bg-popover text-popover-foreground shadow-lg animate-in fade-in slide-in-from-bottom-2 motion-reduce:animate-none"
    >
      {children}
    </div>
  );
}

/** Interrompe a subida do Tab para o laço de foco do Popover; a ação padrão do navegador segue. */
function letTabLeave(event) {
  if (event.key === "Tab") event.stopPropagation();
}
